import type { ColumnFiltersState } from '@tanstack/react-table';
import { format1 } from 'utils/number';
import type { AfeCatalogParams, AfeSourceFilter } from './atoms';
import type { AfeCatalogResult, FlatAfeRow } from './types';

export { AFE_TYPE_LABELS } from './types';

// ─────────────────────────────────────
// Default Params
// ─────────────────────────────────────

/** Default payload used to trigger the initial fetch on mount. */
export const DEFAULT_PARAMS: AfeCatalogParams = {
  page: 1,
  pageSize: 100,
  sortAttribute: 'id',
  sortDirection: 'DESC',
};

// ─────────────────────────────────────
// Data Flattening
// ─────────────────────────────────────

/**
 * Flattens API results by extracting nested well, entity, and file fields
 * into flat columns. Parses string lateralLength/tvd to numbers.
 * Preserves `afeNumber` and `sourceDocument` for the Source column.
 * @param {AfeCatalogResult[]} results - Raw API results.
 * @return {FlatAfeRow[]} Flattened rows for DataTable consumption.
 */
export const flattenResults = (results: AfeCatalogResult[]): FlatAfeRow[] =>
  results.map((row) => {
    const senders = row.entities.filter((e) => e.role === 'sender');
    const receivers = row.entities.filter((e) => e.role === 'receiver');
    const { well, lateralLength, tvd, verificationState, ...rest } = row;

    return {
      ...rest,
      wellType: well.type,
      slotId: well.slotId,
      wellName: well.name,
      wellHref: well.href,
      lateralLength: lateralLength !== null ? parseFloat(lateralLength) : null,
      tvd: tvd !== null ? parseFloat(tvd) : null,
      verificationState: verificationState,
      senders,
      receivers,
    };
  });

/** Source filter UI options for the Source column filter. */
export const SOURCE_FILTER_OPTIONS: {
  value: AfeSourceFilter;
  label: string;
}[] = [
  { value: 'auto_connected', label: 'Auto-Connected' },
  { value: 'manual', label: 'Manual' },
];

/** Maps well_type enum values to display labels. */
export const WELL_TYPE_LABELS: Record<string, string> = {
  existing_well: 'Existing Well',
  generic_well: 'Imported Well',
};

/**
 * Formats a byte count as a human-readable size string with B/KB/MB/GB units.
 * @param {number} bytes - Size in bytes.
 * @return {string} Formatted size (e.g. "512 B", "1.5 KB", "2.3 MB").
 */
export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${format1(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${format1(mb)} MB`;
  return `${format1(mb / 1024)} GB`;
};

// ─────────────────────────────────────
// Column Filter Conversion
// ─────────────────────────────────────

/**
 * Converts TanStack ColumnFiltersState to AfeCatalogParams filter fields.
 * Clears all filter params first, then sets active ones.
 * @param {ColumnFiltersState} filters - The column filter state array.
 * @return {Partial<AfeCatalogParams>} The converted API params.
 */
export const columnFiltersToParams = (
  filters: ColumnFiltersState
): Partial<AfeCatalogParams> => {
  const params: Partial<AfeCatalogParams> = {
    wellTypes: undefined,
    afeTypes: undefined,
    basinNames: undefined,
    states: undefined,
    counties: undefined,
    operators: undefined,
    reportedFormations: undefined,
    source: undefined,
    dateFilter: undefined,
  };

  for (const filter of filters) {
    const value = filter.value as string[];

    switch (filter.id) {
      case 'wellType':
        params.wellTypes = value;
        break;
      case 'afeType':
        params.afeTypes = value;
        break;
      case 'basinName':
        params.basinNames = value;
        break;
      case 'state':
        params.states = value;
        break;
      case 'countyName':
        params.counties = value;
        break;
      case 'operator':
        params.operators = value;
        break;
      case 'reportedFormation':
        params.reportedFormations = value;
        break;
      case 'source': {
        const first = Array.isArray(filter.value)
          ? filter.value[0]
          : filter.value;
        params.source =
          first === 'auto_connected' || first === 'manual' ? first : undefined;
        break;
      }
      case 'afeDate': {
        const [start, end] =
          (filter.value as [string | null, string | null]) ?? [];
        params.dateFilter =
          start || end
            ? {
                type: 'afe_date',
                start: start ?? undefined,
                end: end ?? undefined,
              }
            : undefined;
        break;
      }
    }
  }

  return params;
};

/**
 * Converts AfeCatalogParams to TanStack ColumnFiltersState for initializing
 * or restoring column filter state from API params.
 * @param {AfeCatalogParams | undefined} payload - The current API params.
 * @return {ColumnFiltersState} The column filter state array.
 */
export const paramsToColumnFilters = (
  payload: AfeCatalogParams | undefined
): ColumnFiltersState => {
  if (!payload) return [];

  const filters: ColumnFiltersState = [];

  if (payload.wellTypes?.length) {
    filters.push({ id: 'wellType', value: payload.wellTypes });
  }
  if (payload.afeTypes?.length) {
    filters.push({ id: 'afeType', value: payload.afeTypes });
  }
  if (payload.basinNames?.length) {
    filters.push({ id: 'basinName', value: payload.basinNames });
  }
  if (payload.states?.length) {
    filters.push({ id: 'state', value: payload.states });
  }
  if (payload.counties?.length) {
    filters.push({ id: 'countyName', value: payload.counties });
  }
  if (payload.operators?.length) {
    filters.push({ id: 'operator', value: payload.operators });
  }
  if (payload.reportedFormations?.length) {
    filters.push({
      id: 'reportedFormation',
      value: payload.reportedFormations,
    });
  }
  if (payload.dateFilter?.start || payload.dateFilter?.end) {
    filters.push({
      id: 'afeDate',
      value: [payload.dateFilter.start ?? null, payload.dateFilter.end ?? null],
    });
  }
  if (payload.source) {
    filters.push({ id: 'source', value: [payload.source] });
  }

  return filters;
};
